"use client";

import { PopoverRoot } from "@faire/slate/private/popover/PopoverRoot";
import {
  useDismiss,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import React, { useEffect } from "react";

import {
  FloatingContext,
  useFloatingContext,
} from "../floating/FloatingContext";
import { PopoverContext } from "../popover/PopoverContext";

import type { TooltipRole } from "./types";
import { getTooltipRole } from "./utils";

type IProps = {
  /**
   * Tooltip role for assistive technologies. Ignored when
   * `announceToAssistiveTechnology` is false.
   */
  role?: TooltipRole;
  /**
   * When false, the tooltip is hover-only for sighted users and does not wire
   * `aria-describedby` or focus interactions. Use when description is provided
   * elsewhere (for example `aria-description` on a table header cell).
   *
   * @default true
   */
  announceToAssistiveTechnology?: boolean;
} & Omit<
  React.ComponentProps<typeof PopoverRoot>,
  "role" | "open" | "onOpenChange" | "initiallyOpen"
>;

export const TooltipRoot: React.FC<React.PropsWithChildren<IProps>> = ({
  children,
  role,
  announceToAssistiveTechnology = true,
  ...contextOptions
}) => {
  return (
    <FloatingContext>
      <PopoverContext {...contextOptions}>
        <Root
          role={role}
          announceToAssistiveTechnology={announceToAssistiveTechnology}
        >
          {children}
        </Root>
      </PopoverContext>
    </FloatingContext>
  );
};

const Root: React.FC<
  React.PropsWithChildren<{
    role: TooltipRole | undefined;
    announceToAssistiveTechnology: boolean;
  }>
> = ({ children, role: roleProp, announceToAssistiveTechnology }) => {
  const { floatingRootContext, setInteractions } = useFloatingContext();

  const role = useRole(floatingRootContext, {
    role: roleProp ? getTooltipRole(roleProp) : "tooltip",
    enabled: announceToAssistiveTechnology && Boolean(roleProp),
  });
  const dismiss = useDismiss(floatingRootContext);
  const hover = useHover(floatingRootContext, {
    move: false,
  });
  const focus = useFocus(floatingRootContext, {
    enabled: announceToAssistiveTechnology,
  });

  const interactions = useInteractions([dismiss, role, hover, focus]);

  useEffect(() => {
    setInteractions(interactions);
  }, [interactions, setInteractions]);

  return children;
};
